import { action } from 'typesafe-actions';

import { HitData } from 'common/store/common/search/types';
import { ContentLanguagesDefinition } from 'common/services/i18nService';
import {
    AdditionalResource,
    AlternativesLimitErrorMsg, ContentEditStatus,
    ContextTypeKeys,
    ItemBankInfo,
    ItemContext,
    QTIContextItem,
} from './types';
import { DynamicAnswerKeyEvents } from './reducer';
import { Assessment } from '../../features/questionDelivery/types';

export interface SetCurrentItemContext {
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT;
    contextId: number;
    currentItemContext: ItemContext;
}

export interface SetTemplateInCurrentItemContext {
    type: ContextTypeKeys.SET_TEMPLATE_IN_CURRENT_ITEM_CONTEXT;
    isTemplate: boolean;
}

export interface SetActiveLanguage {
    type: ContextTypeKeys.SET_ACTIVE_LANGUAGE;
    activeLanguage: ContentLanguagesDefinition;
}

export interface SetQuestionSupportedLanguages {
    type: ContextTypeKeys.SET_QUESTION_SUPPORTED_LANGUAGES;
    supportedLanguages: ContentLanguagesDefinition[];
}
export interface SetActiveContentLanguage {
    type: ContextTypeKeys.SET_ACTIVE_CONTENT_LANGUAGE;
    activeContentLanguage: ContentLanguagesDefinition;
    // When false, the reducer will NOT write the language to localStorage.
    // Used by auto-initialization code paths (e.g. ContentEditorHeader
    // selecting a default when none is set) so they don't clobber the
    // user's previously persisted per-question selection.
    persist?: boolean;
}
export interface SetQuestionSetSupportedLanguages {
    type: ContextTypeKeys.SET_QUESTION_SET_SUPPORTED_LANGUAGES;
    supportedLanguages: ContentLanguagesDefinition[];
}

export interface SetCurrentItemContextLock {
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_LOCK;
    isLocked: boolean;
}

export interface SetCurrentItemContextDirty {
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_DIRTY;
    isDirty: boolean;
}

export interface UnLockContext {
    type: ContextTypeKeys.UNLOCK_CONTEXT;
    navigateBackToLists: boolean;
}

export interface SetCurrentQtiContextItem {
    type: ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM;
    qtiContextItemId: number;
    currentQtiContextItem: QTIContextItem;
    oldQtiContextItem: QTIContextItem | number | undefined;
}

export interface SetCurrentResourceContextItem {
    type: ContextTypeKeys.SET_CURRENT_RESOURCE_CONTEXT_ITEM;
    payload: AdditionalResource;
}

export interface SetItemBankInfo {
    type: ContextTypeKeys.SET_ITEM_BANK_INFO;
    itemBankId: number;
    itemBankInfo: ItemBankInfo;
}

export interface SetContextItemBankInfoData {
    type: ContextTypeKeys.SET_CONTEXT_ITEM_BANK_INFO;
    itemBankInfo: ItemBankInfo;
}

export interface SetItemBankForbidden {
    type: ContextTypeKeys.SET_ITEM_BANK_FORBIDDEN;
    isForbidden: boolean;
}

export interface SetContextEditStatusAction {
    contextId: number;
    contentEditStatus: ContentEditStatus;
    type: ContextTypeKeys.SET_CONTEXT_EDIT_STATUS;
}

export interface ResetContextEditStatusAction {
    contextId: number;
    type: ContextTypeKeys.RESET_CONTEXT_EDIT_STATUS;
}

export interface ShowContextLockModal {
    contextId: number;
    type: ContextTypeKeys.SHOW_CONTEXT_LOCK_MODAL;
}

export interface CloseContextLockModal {
    contextId: number;
    type: ContextTypeKeys.CLOSE_CONTEXT_LOCK_MODAL;
}

export interface SetContentItems {
    type: ContextTypeKeys.SET_CONTENT_ITEMS;
    contentItems: HitData[];
}

export interface ToggleNavigationLock {
    type: ContextTypeKeys.TOGGLE_NAVIGATION_LOCK;
    payload: boolean;
}

export interface TogglePropertiesPanel {
    type: ContextTypeKeys.TOGGLE_PROPERTIES_PANEL;
    payload: boolean;
}

export interface SetWorkspaceId {
    type: ContextTypeKeys.SET_WORKSPACE_ID;
    workspaceId: string;
}

export interface SetItemBankId {
    type: ContextTypeKeys.SET_ITEM_BANK_ID;
    itemBankId: number;
}

export interface SetIsIBTemplate {
    type: ContextTypeKeys.SET_IS_IB_TEMPLATE;
    isIBTemplate: boolean;
}

export interface SetContentItemIds {
    type: ContextTypeKeys.SET_CONTENT_ITEM_IDS;
    contentItemIds: number[] | number;
}

export interface SetQuestionIdAndScorableItemAndSectionId {
    type: ContextTypeKeys.SET_QUESTION_ID_AND_SCORABLE_ITEM_AND_SECTION_ID;
    questionId: number;
    scorableItem: number;
    sectionId: number;
}

export interface ResetQuestionIdAndScorableItemAndSectionId {
    type: ContextTypeKeys.RESET_QUESTION_ID_AND_SCOREABLE_ITEM_AND_SECTION_ID;
}

export interface RestoreCurrentQtiContextItem {
    type: ContextTypeKeys.RESTORE_CURRENT_QTI_CONTEXT_ITEM;
}

export interface SetCurrentQtiContextItemMetaData {
    type: ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM_META_DATA;
    questionMetaData: any;
}

export interface SetKeyEvent {
    type: ContextTypeKeys.SET_DYNAMIC_ANSWER_KEY_EVENT;
    dakKeyEvent: DynamicAnswerKeyEvents;
}

export type RenameContextItem = {
    type: ContextTypeKeys.CONTEXT_RENAME;
    title: any;
}

export interface SetIsQTIContextLocked {
    type: ContextTypeKeys.SET_IS_QTI_CONTEXT_LOCKED;
    isQTIContextLocked: boolean | null;
}

export interface FetchAdditionalContextItemData {
    type: ContextTypeKeys.FETCH_ADDITIONAL_CONTEXT_ITEM_DATA;
    objectId: number;
    itemBankId?: number;
}

export interface FetchContextItemBankInfo {
    type: ContextTypeKeys.FETCH_CONTEXT_ITEM_BANK_INFO;
    itemBankId: number;
}

export interface SetContextQtiItemPublishStatus {
    type: ContextTypeKeys.SET_CONTEXT_QTI_ITEM_PUBLISH_STATUS;
    publishStatus: string;
}

export interface SetContextQtiItemBankId {
    type: ContextTypeKeys.SET_CONTEXT_QTI_ITEM_BANK_ID;
    itemBankId: number;
}

export interface SetMaxAnswersWarning {
    type: ContextTypeKeys.SET_MAX_ANSWERS_WARNING;
    maxAnswersWarningMessage: AlternativesLimitErrorMsg;
}

export interface GetAssessmentAction {
    type: ContextTypeKeys.GET_ASSESSMENT;
    assessmentId: string;
}
export interface GetAssessmentSuccessAction {
    type: ContextTypeKeys.GET_ASSESSMENT_SUCCESS;
    payload: Assessment;
}

export interface GetAssessmentDetailsAction {
    type: ContextTypeKeys.GET_ASSESSMENT_DETAILS;
}
export interface GetAssessmentDetailsSuccessAction {
    type: ContextTypeKeys.GET_ASSESSMENT_DETAILS_SUCCESS;
    payload: any;
  }

export interface UpdateAssessmentAction {
    type: ContextTypeKeys.UPDATE_ASSESSMENT;
    assessmentId: string;
    assessmentData: Assessment;
    onSuccessCallback?: () => void;
}

export interface UpdateAssessmentSuccessAction {
    type: ContextTypeKeys.UPDATE_ASSESSMENT_SUCCESS;
}

export interface BulkUpdateAssessmentAction {
    type: ContextTypeKeys.BULK_UPDATE_ASSESSMENT;
    assessmentId: string;
    assessmentData: Assessment;
    numberOfQuestionsNeedingUpdate: number;
    featureId: string;
}
export interface ShowBulkUpdateSuccessNotificationAction {
    type: ContextTypeKeys.SHOW_BULK_UPDATE_SUCCESS_NOTIFICATION;
    count: number;
}
export interface ClearBulkUpdateSuccessNotificationAction {
    type: ContextTypeKeys.CLEAR_BULK_UPDATE_SUCCESS_NOTIFICATION;
}

export interface UpdateView {
    type: ContextTypeKeys.VIEW_TYPE;
    payload: 'desktop' | 'laptop' | 'mobile' | 'fileText';
  }
export interface AddContentBlockSelectionAction {
    type: ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION;
    assessmentId: string;
    payload: any;
}

export interface AddContentBlockSelectionSuccessAction {
    type: ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION_SUCCESS;
}

export interface UpdateContentInBulkResponse {
    success: boolean;
    // eslint-disable-next-line camelcase
    pushChannel_itemsIndex_context?: string;
    // eslint-disable-next-line camelcase
    pushChannel_contextIndex_context?: string;
}
export interface RubricSaveMessageData {
    type: string;
    contentItemId?: number;
    contentRevisionId?: number;
    structureEditorFeatureId?: string;
    skipOverlayClose?: boolean;
    pushChannel?: string;
}

export interface RubricSavedToQuestionAction {
    type: ContextTypeKeys.RUBRIC_SAVED_TO_QUESTION;
    payload: RubricSaveMessageData;
}
export interface RubricClosedAction {
    type: ContextTypeKeys.CLOSE_EDIT_RUBRIC_OVERLAY;
    payload: RubricSaveMessageData;
}

export interface SupportedLanguagesSavedAction {
    type: ContextTypeKeys.SUPPORTED_LANGUAGES_SAVED;
}

export interface QuestionLanguageUpdateCompleteAction {
    type: ContextTypeKeys.QUESTION_LANGUAGE_UPDATE_COMPLETE;
}

export interface SetCurrentQtcSectionData {
    type: ContextTypeKeys.SET_CURRENT_QTC_SECTION_DATA;
    sectionData: any;
}

export type ActionTypes = SetContentItems
    | SetCurrentItemContext
    | SetTemplateInCurrentItemContext
    | FetchAdditionalContextItemData
    | SetCurrentQtiContextItem
    | SetQuestionIdAndScorableItemAndSectionId
    | ResetQuestionIdAndScorableItemAndSectionId
    | SetContentItemIds
    | SetWorkspaceId
    | SetItemBankId
    | SetItemBankInfo
    | ToggleNavigationLock
    | TogglePropertiesPanel
    | SetCurrentItemContextLock
    | SetContextEditStatusAction
    | ResetContextEditStatusAction
    | ShowContextLockModal
    | CloseContextLockModal
    | SetCurrentItemContextDirty
    | UnLockContext
    | SetCurrentResourceContextItem
    | RestoreCurrentQtiContextItem
    | SetKeyEvent
    | RenameContextItem
    | SetIsIBTemplate
    | SetCurrentQtiContextItemMetaData
    | SetActiveLanguage
    | SetQuestionSupportedLanguages
    | SetActiveContentLanguage
    | SetQuestionSetSupportedLanguages
    | SetIsQTIContextLocked
    | SetContextQtiItemPublishStatus
    | SetContextQtiItemBankId
    | FetchContextItemBankInfo
    | SetContextItemBankInfoData
    | SetItemBankForbidden
    | SetMaxAnswersWarning
    | GetAssessmentAction
    | GetAssessmentSuccessAction
    | UpdateAssessmentAction
    | UpdateAssessmentSuccessAction
    | BulkUpdateAssessmentAction
    | ShowBulkUpdateSuccessNotificationAction
    | ClearBulkUpdateSuccessNotificationAction
    | UpdateView
    | GetAssessmentDetailsAction
    | GetAssessmentDetailsSuccessAction
    | AddContentBlockSelectionAction
    | AddContentBlockSelectionSuccessAction
    | RubricSavedToQuestionAction
    | RubricClosedAction
    | SupportedLanguagesSavedAction
    | QuestionLanguageUpdateCompleteAction
    | SetCurrentQtcSectionData;

export const setIsQTIContextLocked = (isQTIContextLocked: boolean): SetIsQTIContextLocked => ({
    type: ContextTypeKeys.SET_IS_QTI_CONTEXT_LOCKED,
    isQTIContextLocked,
});

export const fetchAdditionalContextItemData = (objectId: number, itemBankId?: number): FetchAdditionalContextItemData => ({
    type: ContextTypeKeys.FETCH_ADDITIONAL_CONTEXT_ITEM_DATA,
    objectId,
    itemBankId,
});

export const fetchContextItemBankInfo = (itemBankId: number): FetchContextItemBankInfo => ({
    type: ContextTypeKeys.FETCH_CONTEXT_ITEM_BANK_INFO,
    itemBankId,
});

export const setContextQtiItemPublishStatus = (publishStatus: string): SetContextQtiItemPublishStatus => ({
    type: ContextTypeKeys.SET_CONTEXT_QTI_ITEM_PUBLISH_STATUS,
    publishStatus,
});

export const setContextQtiItemBankId = (itemBankId: number): SetContextQtiItemBankId => ({
    type: ContextTypeKeys.SET_CONTEXT_QTI_ITEM_BANK_ID,
    itemBankId,
});

export const setDynamicAnswerKeyEvent = (
    dakKeyEvent?: DynamicAnswerKeyEvents,
): SetKeyEvent => ({
    type: ContextTypeKeys.SET_DYNAMIC_ANSWER_KEY_EVENT,
    dakKeyEvent,
});

export const workspaceIdUpdated = () => {
    return action(ContextTypeKeys.WORKSPACE_ID_UPDATED);
};

export const setupWorkspaces = () => {
    return action(ContextTypeKeys.SETUP_WORKSPACES);
};

export const setQuestionIdAndScorableItemAndSectionId = (questionId: number, scorableItem: number, sectionId: number):
 SetQuestionIdAndScorableItemAndSectionId => ({
    type: ContextTypeKeys.SET_QUESTION_ID_AND_SCORABLE_ITEM_AND_SECTION_ID,
    questionId,
    scorableItem,
    sectionId,
});

export const setItemBankId = (itemBankId: number): SetItemBankId => ({
    type: ContextTypeKeys.SET_ITEM_BANK_ID,
    itemBankId,
});

export const setIsIBTemplate = (isIBTemplate: boolean): SetIsIBTemplate => ({
    type: ContextTypeKeys.SET_IS_IB_TEMPLATE,
    isIBTemplate,
});

export const setWorkspaceId = (workspaceId: string): SetWorkspaceId => ({
    type: ContextTypeKeys.SET_WORKSPACE_ID,
    workspaceId,
});

export const setContentItems = (contentItems: HitData[]): SetContentItems => ({
    type: ContextTypeKeys.SET_CONTENT_ITEMS,
    contentItems,
});

export const setContentItemIds = (contentItemIds: number[] | number): SetContentItemIds => ({
    type: ContextTypeKeys.SET_CONTENT_ITEM_IDS,
    contentItemIds,
});

export const setCurrentItemContext = (contextId: number, currentItemContext: ItemContext): SetCurrentItemContext => ({
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT,
    contextId,
    currentItemContext,
});

export const setTemplateInCurrentItemContext = (isTemplate: boolean): SetTemplateInCurrentItemContext => ({
    type: ContextTypeKeys.SET_TEMPLATE_IN_CURRENT_ITEM_CONTEXT,
    isTemplate,
});

export const setCurrentItemContextLock = (isLocked: boolean): SetCurrentItemContextLock => ({
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_LOCK,
    isLocked,
});

export const setCurrentQtiContextItem = (
    qtiContextItemId: number,
    currentQtiContextItem: QTIContextItem,
    oldQtiContextItem: QTIContextItem | number | undefined,
): SetCurrentQtiContextItem => ({
    type: ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM,
    qtiContextItemId,
    currentQtiContextItem,
    oldQtiContextItem,
});

export const setCurrentResourceContextItem = (
    payload: AdditionalResource
): SetCurrentResourceContextItem => ({
    type: ContextTypeKeys.SET_CURRENT_RESOURCE_CONTEXT_ITEM,
    payload,
});

export const setContextEditStatus = (contextId: number, contentEditStatus: ContentEditStatus): SetContextEditStatusAction => ({
    contextId,
    contentEditStatus,
    type: ContextTypeKeys.SET_CONTEXT_EDIT_STATUS,
});

export const resetContextEditStatus = (contextId: number): ResetContextEditStatusAction => ({
    contextId,
    type: ContextTypeKeys.RESET_CONTEXT_EDIT_STATUS,
});

export const showContextLockModal = (contextId: number): ShowContextLockModal => ({
    contextId,
    type: ContextTypeKeys.SHOW_CONTEXT_LOCK_MODAL,
});

export const closeContextLockModal = (contextId: number): CloseContextLockModal => ({
    contextId,
    type: ContextTypeKeys.CLOSE_CONTEXT_LOCK_MODAL,
});

export const setItemBankInfo = (itemBankId: number, itemBankInfo: ItemBankInfo): SetItemBankInfo => ({
    type: ContextTypeKeys.SET_ITEM_BANK_INFO,
    itemBankId,
    itemBankInfo,
});

export const setContextItemBankInfoData = (itemBankInfo: ItemBankInfo): SetContextItemBankInfoData => ({
    type: ContextTypeKeys.SET_CONTEXT_ITEM_BANK_INFO,
    itemBankInfo,
});

export const setItemBankForbidden = (isForbidden: boolean): SetItemBankForbidden => ({
    type: ContextTypeKeys.SET_ITEM_BANK_FORBIDDEN,
    isForbidden,
});

export const setDirtyCurrentQtiContextItem = (isDirty: boolean): SetCurrentItemContextDirty => ({
    isDirty,
    type: ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT_DIRTY,
});

export const resetQuestionIdAndScorableItemAndSectionId = (): ResetQuestionIdAndScorableItemAndSectionId => ({
    type: ContextTypeKeys.RESET_QUESTION_ID_AND_SCOREABLE_ITEM_AND_SECTION_ID,
});

export const updateCurrentItemContext = (skipLockModal?: boolean) => {
    return action(ContextTypeKeys.UPDATE_CURRENT_ITEM_CONTEXT, skipLockModal);
};

export const updateCurrentQtiContextItem = () => {
    return action(ContextTypeKeys.UPDATE_CURRENT_QTI_CONTEXT_ITEM);
};

export const supportedLanguagesSaved = () => {
    return action(ContextTypeKeys.SUPPORTED_LANGUAGES_SAVED);
};

export const questionLanguageUpdateComplete = () => {
    return action(ContextTypeKeys.QUESTION_LANGUAGE_UPDATE_COMPLETE);
};

export const toggleNavigationLock = (navigationLock: boolean) => {
    return action(ContextTypeKeys.TOGGLE_NAVIGATION_LOCK, navigationLock);
};

export const restoreCurrentQtiContextItem = (): RestoreCurrentQtiContextItem => ({
    type: ContextTypeKeys.RESTORE_CURRENT_QTI_CONTEXT_ITEM,
});

export const togglePropertiesPanel = () => action(ContextTypeKeys.TOGGLE_PROPERTIES_PANEL);

export const unLockContext = (navigateBackToLists: boolean): UnLockContext => ({
    navigateBackToLists,
    type: ContextTypeKeys.UNLOCK_CONTEXT,
});

export const renameContextItem = (title: any): RenameContextItem => ({
    title,
    type: ContextTypeKeys.CONTEXT_RENAME,
});

export const setCurrentQtiContextItemMetaData = (questionMetaData: any): SetCurrentQtiContextItemMetaData => ({
    type: ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM_META_DATA,
    questionMetaData,
});

export const setActiveLanguage = (activeLanguage: ContentLanguagesDefinition): SetActiveLanguage => ({
    type: ContextTypeKeys.SET_ACTIVE_LANGUAGE,
    activeLanguage,
});

export const setQuestionSupportedLanguages = (supportedLanguages: ContentLanguagesDefinition[]): SetQuestionSupportedLanguages => ({
    type: ContextTypeKeys.SET_QUESTION_SUPPORTED_LANGUAGES,
    supportedLanguages,
});

export const setActiveContentLanguage = (
    activeContentLanguage: ContentLanguagesDefinition,
    persist: boolean = true,
): SetActiveContentLanguage => ({
    type: ContextTypeKeys.SET_ACTIVE_CONTENT_LANGUAGE,
    activeContentLanguage,
    persist,
});

export const setQuestionSetSupportedLanguages = (supportedLanguages: ContentLanguagesDefinition[]): SetQuestionSetSupportedLanguages => ({
    type: ContextTypeKeys.SET_QUESTION_SET_SUPPORTED_LANGUAGES,
    supportedLanguages,
});

export const setMaxAnswersWarning = (maxAnswersWarningMessage: AlternativesLimitErrorMsg): SetMaxAnswersWarning => ({
    type: ContextTypeKeys.SET_MAX_ANSWERS_WARNING,
    maxAnswersWarningMessage,
});

export const getAssessment = (assessmentId: string): GetAssessmentAction => ({
    type: ContextTypeKeys.GET_ASSESSMENT,
    assessmentId,
});

export const getAssessmentSuccess = (assessmentData: Assessment): GetAssessmentSuccessAction => ({
    type: ContextTypeKeys.GET_ASSESSMENT_SUCCESS,
    payload: assessmentData,
});

export const getAssessmentDetails = (): GetAssessmentDetailsAction => ({
    type: ContextTypeKeys.GET_ASSESSMENT_DETAILS,
});

export const getAssessmentDetailsSuccess = (payload: any): GetAssessmentDetailsSuccessAction => ({
    type: ContextTypeKeys.GET_ASSESSMENT_DETAILS_SUCCESS,
    payload,
});

export const updateAssessment = (
    assessmentId: string,
    assessmentData: any,
    onSuccessCallback?: () => void,
): UpdateAssessmentAction => ({
    type: ContextTypeKeys.UPDATE_ASSESSMENT,
    assessmentId,
    assessmentData,
    onSuccessCallback,
});

export const updateAssessmentSuccess = (): UpdateAssessmentSuccessAction => ({
    type: ContextTypeKeys.UPDATE_ASSESSMENT_SUCCESS,
});

export const bulkUpdateAssessment = (
    assessmentId: string,
    assessmentData: Assessment,
    numberOfQuestionsNeedingUpdate: number,
    featureId: string,
): BulkUpdateAssessmentAction => ({
    type: ContextTypeKeys.BULK_UPDATE_ASSESSMENT,
    assessmentId,
    assessmentData,
    numberOfQuestionsNeedingUpdate,
    featureId,
});

export const showBulkUpdateSuccessNotification = (count: number): ShowBulkUpdateSuccessNotificationAction => ({
    type: ContextTypeKeys.SHOW_BULK_UPDATE_SUCCESS_NOTIFICATION,
    count,
});

export const clearBulkUpdateSuccessNotification = (): ClearBulkUpdateSuccessNotificationAction => ({
    type: ContextTypeKeys.CLEAR_BULK_UPDATE_SUCCESS_NOTIFICATION,
});

export const updateView = (
    viewType: 'desktop' | 'laptop' | 'mobile' | 'fileText'
  ): UpdateView => ({
    type: ContextTypeKeys.VIEW_TYPE,
    payload: viewType,
  });
export const addContentBlockSelection = (
    assessmentId: string,
    payload: any
): AddContentBlockSelectionAction => ({
    type: ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION,
    assessmentId,
    payload,
});

export const addContentBlockSelectionSuccess = (): AddContentBlockSelectionSuccessAction => ({
    type: ContextTypeKeys.ADD_CONTENT_BLOCK_SELECTION_SUCCESS,
});

export const rubricSavedToQuestion = (payload: RubricSaveMessageData): RubricSavedToQuestionAction => ({
    type: ContextTypeKeys.RUBRIC_SAVED_TO_QUESTION,
    payload,
});

export const rubricClosed = (payload: RubricSaveMessageData): RubricClosedAction => ({
    type: ContextTypeKeys.CLOSE_EDIT_RUBRIC_OVERLAY,
    payload,
});

export const setCurrentQtcSectionData = (
    sectionData: any
): SetCurrentQtcSectionData => ({
    type: ContextTypeKeys.SET_CURRENT_QTC_SECTION_DATA,
    sectionData,
});
